import type { ReportGenerationPort } from "@/application/records/ports/ReportGenerationPort";
import {
  GenerationDomainService,
  type GenerationConfigSnapshot,
} from "@/domain/records/services/GenerationDomainService";
import { ValidationService } from "@/domain/records/services/ValidationService";
import {
  isTransportError,
  type TransportError,
} from "@/infrastructure/http/transport-error";
import type {
  AvailableVersions,
  RecordId,
  ReportType,
} from "@/domain/records/types";

/**
 * Use Case for generating reports
 * Clean Architecture - Application Layer
 */
export class GenerateReportUseCase {
  constructor(
    private readonly reportGenerationAdapter: ReportGenerationPort,
    private readonly generationDomainService: GenerationDomainService
  ) {}

  /**
   * Generate new reports
   */
  async generateReports(
    recordId: RecordId,
    config: {
      reportTypes: string[];
      customPrompt?: string;
      goals?: string;
      guide?: string;
      notes?: string;
      participantCount?: number;
      respondentNames: string[];
      language?: string;
      reportLanguage?: string;
      sttProvider?: "auto" | "assembly" | "soniox";
      isWithTranscript: boolean;
      isWithInsightReport: boolean;
    }
  ): Promise<{
    success: boolean;
    error?: string;
    estimatedTime?: number;
    pendingConfirmation?: boolean;
  }> {
    let estimatedSeconds: number | undefined;

    try {
      // Create domain request object
      const request = this.generationDomainService.createGenerationRequest(
        recordId,
        {
          ...config,
          reportTypes: this.normalizeReportTypes(config.reportTypes),
          language: config.language,
          reportLanguage: config.reportLanguage,
        }
      );

      // Validate request
      const validation = ValidationService.validateGenerationRequest(request);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.errors.join(", "),
        };
      }

      // Get processing time estimate
      ({ estimatedSeconds } =
        this.generationDomainService.estimateProcessingTime(request));

      // Execute generation
      await this.reportGenerationAdapter.generate(request);

      return {
        success: true,
        estimatedTime: estimatedSeconds,
      };
    } catch (error) {
      if (this.isPendingConfirmationTimeout(error)) {
        const confirmation = await this.confirmStartAfterTimeout(
          recordId,
          error,
          "generation"
        );

        if (!confirmation.confirmed) {
          return {
            success: false,
            error: confirmation.error,
          };
        }

        return {
          success: true,
          estimatedTime: estimatedSeconds,
          pendingConfirmation: true,
        };
      }

      console.error("Failed to generate reports:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to generate reports",
      };
    }
  }

  /**
   * Regenerate existing reports
   */
  async regenerateReports(
    recordId: RecordId,
    config: {
      reportTypes: string[];
      customPrompt?: string;
      goals?: string;
      guide?: string;
      notes?: string;
      participantCount?: number;
      respondentNames: string[];
      language?: string;
      reportLanguage?: string;
      sttProvider?: "auto" | "assembly" | "soniox";
      isWithTranscript: boolean;
      isWithInsightReport: boolean;
    }
  ): Promise<{
    success: boolean;
    error?: string;
    pendingConfirmation?: boolean;
  }> {
    try {
      const request = this.generationDomainService.createGenerationRequest(
        recordId,
        {
          ...config,
          reportTypes: this.normalizeReportTypes(config.reportTypes),
          language: config.language,
          reportLanguage: config.reportLanguage,
          isWithTranscript: config.isWithTranscript,
          isWithInsightReport: config.isWithInsightReport,
          participantCount: config.participantCount,
          respondentNames: config.respondentNames,
        }
      );

      await this.reportGenerationAdapter.regenerate(request);

      return { success: true };
    } catch (error) {
      if (this.isPendingConfirmationTimeout(error)) {
        const confirmation = await this.confirmStartAfterTimeout(
          recordId,
          error,
          "regeneration"
        );

        if (!confirmation.confirmed) {
          return {
            success: false,
            error: confirmation.error,
          };
        }

        return {
          success: true,
          pendingConfirmation: true,
        };
      }

      console.error("Failed to regenerate reports:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to regenerate reports",
      };
    }
  }

  /**
   * Cancel ongoing generation
   */
  async cancelGeneration(recordId: RecordId): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      await this.reportGenerationAdapter.cancel(recordId);
      return { success: true };
    } catch (error) {
      console.error("Failed to cancel generation:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to cancel generation",
      };
    }
  }

  /**
   * Get generation status
   */
  async getGenerationStatus(recordId: RecordId): Promise<{
    status: string;
    progress?: number;
    message?: string;
    error?: string;
  }> {
    try {
      return await this.reportGenerationAdapter.getStatus(recordId);
    } catch (error) {
      console.error("Failed to get generation status:", error);
      return {
        status: "error",
        error: error instanceof Error ? error.message : "Failed to get status",
      };
    }
  }

  /**
   * Get processing time estimate
   */
  getProcessingEstimate(config: {
    reportTypes: string[];
    customPrompt?: string;
    respondentNames: string[];
  }) {
    const request = this.generationDomainService.createGenerationRequest("", {
      ...config,
      reportTypes: this.normalizeReportTypes(config.reportTypes),
      goals: "",
      notes: "",
      language: undefined,
      reportLanguage: undefined,
      isWithTranscript: false,
      isWithInsightReport: true,
      participantCount: undefined,
    });

    return this.generationDomainService.estimateProcessingTime(request);
  }

  /**
   * Check if regeneration is needed
   */
  isRegenerationNeeded(
    currentConfig: GenerationConfigSnapshot,
    newConfig: GenerationConfigSnapshot,
    availableVersions: AvailableVersions | null | undefined
  ): boolean {
    return this.generationDomainService.isRegenerationNeeded(
      currentConfig,
      newConfig,
      availableVersions
    );
  }

  /**
   * Get recommended report types
   */
  getRecommendedReportTypes(contentType: "audio" | "video" | "text") {
    return this.generationDomainService.getRecommendedReportTypes(contentType);
  }

  private normalizeReportTypes(reportTypes: string[]): ReportType[] {
    return reportTypes.map((type) => type as ReportType);
  }

  private isPendingConfirmationTimeout(
    error: unknown
  ): error is TransportError {
    return isTransportError(error) && error.code === "TIMEOUT";
  }

  private async confirmStartAfterTimeout(
    recordId: RecordId,
    timeoutError: TransportError,
    mode: "generation" | "regeneration"
  ): Promise<
    | { confirmed: true }
    | {
        confirmed: false;
        error: string;
      }
  > {
    try {
      const status = await this.reportGenerationAdapter.getStatus(recordId);

      if (this.isConfirmedStartStatus(status.status)) {
        console.warn(
          `Report ${mode} start timed out; confirmed by status check`,
          {
            recordId,
            requestId: timeoutError.requestId,
            status: status.status,
          }
        );

        return { confirmed: true };
      }

      console.warn(`Report ${mode} start timed out but was not confirmed`, {
        recordId,
        requestId: timeoutError.requestId,
        status: status.status,
        error: status.error,
        message: status.message,
      });

      return {
        confirmed: false,
        error:
          status.error ??
          status.message ??
          timeoutError.message,
      };
    } catch (statusError) {
      console.warn(
        `Report ${mode} start timed out and confirmation failed`,
        {
          recordId,
          requestId: timeoutError.requestId,
          error: statusError,
        }
      );

      return {
        confirmed: false,
        error: timeoutError.message,
      };
    }
  }

  private isConfirmedStartStatus(status: string | undefined): boolean {
    return status === "in_progress" || status === "completed";
  }
}
